import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

type Status = "checking" | "complete" | "pending" | "incomplete" | "error";

interface AccountDetails {
  currently_due?: string[];
}

export default function ConnectSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // email comes from return_url: /connect-success?email=...
  // fallback to localStorage in case the query param is missing
  const email =
    searchParams.get("email") || localStorage.getItem("user_email") || "";

  // session_id / account_id may also be passed back by Stripe — log for debugging
  const sessionId = searchParams.get("session_id") || "";
  const accountId = searchParams.get("account_id") || "";

  const [status, setStatus] = useState<Status>("checking");
  const [details, setDetails] = useState<AccountDetails>({});
  const [errorMsg, setErrorMsg] = useState("");

  const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

  // ── Check account status on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!email) {
      setStatus("error");
      setErrorMsg("Email not found. Please log in and try again.");
      return;
    }
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async () => {
    setStatus("checking");
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/subscription/account-status`,
        { params: { email } }
      );
      setStatus(data.status as Status);
      setDetails(data.details || {});
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.error || "Failed to verify account status."
      );
    }
  };

  // Resume onboarding by calling connect-stripe again to get a fresh link
  const resumeOnboarding = async () => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/subscription/connect-stripe`,
        { email }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error || "Failed to resume onboarding."
      );
      setStatus("error");
    }
  };

  // ── Shared layout wrapper ─────────────────────────────────────────────────────
  const Wrap = ({ children }: { children: React.ReactNode }) => (
    <div style={styles.page}>
      <div style={styles.card}>{children}</div>
    </div>
  );

  // ── States ────────────────────────────────────────────────────────────────────

  if (status === "checking") {
    return (
      <Wrap>
        <div style={styles.spinner} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={styles.subtitle}>Verifying your Stripe account…</p>
      </Wrap>
    );
  }

  if (status === "complete") {
    return (
      <Wrap>
        <div style={styles.emoji}>🎉</div>
        <h2 style={styles.heading}>Stripe connected!</h2>
        <p style={styles.subtitle}>
          You can now receive payments and payouts.
        </p>
        <button
          style={styles.primaryBtn}
          onClick={() => navigate("/codegenie/generated-blogs")}
        >
          Go to Dashboard
        </button>
      </Wrap>
    );
  }

  if (status === "pending") {
    return (
      <Wrap>
        <div style={styles.emoji}>⏳</div>
        <h2 style={styles.heading}>Almost there</h2>
        <p style={styles.subtitle}>
          Stripe is reviewing your details. We'll notify you once it's ready
          (usually within a few minutes).
        </p>
        {(details?.currently_due?.length ?? 0) > 0 && (
          <div style={styles.dueBadge}>
            <span style={styles.dueLabel}>Still needed:</span>{" "}
            {details.currently_due!.join(", ")}
          </div>
        )}
        <div style={styles.btnRow}>
          <button style={styles.ghostBtn} onClick={checkStatus}>
            Refresh status
          </button>
          <button
            style={styles.primaryBtn}
            onClick={() => navigate("/codegenie/generated-blogs")}
          >
            Go to Dashboard
          </button>
        </div>
      </Wrap>
    );
  }

  if (status === "incomplete") {
    return (
      <Wrap>
        <div style={styles.emoji}>⚠️</div>
        <h2 style={styles.heading}>Onboarding incomplete</h2>
        <p style={styles.subtitle}>
          Looks like you didn't finish the setup. Resume to start receiving
          payments.
        </p>
        <button style={styles.primaryBtn} onClick={resumeOnboarding}>
          Resume Onboarding
        </button>
      </Wrap>
    );
  }

  // error fallback
  return (
    <Wrap>
      <div style={styles.emoji}>❌</div>
      <h2 style={styles.heading}>Something went wrong</h2>
      <p style={styles.subtitle}>{errorMsg}</p>
      <div style={styles.btnRow}>
        <button style={styles.ghostBtn} onClick={checkStatus}>
          Try again
        </button>
        <button
          style={styles.primaryBtn}
          onClick={() => navigate("/codegenie/generated-blogs")}
        >
          Go to Dashboard
        </button>
      </div>
    </Wrap>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#0d0e1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px",
  },
  card: {
    background: "#12131c",
    border: "1px solid #22243a",
    borderRadius: 14,
    padding: "40px 36px",
    maxWidth: 420,
    width: "100%",
    textAlign: "center",
    color: "#fff",
  },
  emoji: {
    fontSize: 42,
    marginBottom: 14,
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 13.5,
    color: "#7c8298",
    lineHeight: 1.6,
    marginBottom: 20,
  },
  dueBadge: {
    background: "#1a1b28",
    border: "1px solid #22243a",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 12,
    color: "#9aa0b4",
    textAlign: "left",
    marginBottom: 20,
  },
  dueLabel: {
    fontWeight: 600,
    color: "#cdd1e0",
  },
  btnRow: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
  },
  primaryBtn: {
    background: "#4f6ef7",
    border: "none",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "10px 22px",
    borderRadius: 7,
    fontFamily: "inherit",
  },
  ghostBtn: {
    background: "none",
    border: "1px solid #2a2c3e",
    color: "#9aa0b4",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    padding: "10px 18px",
    borderRadius: 7,
    fontFamily: "inherit",
  },
  spinner: {
    width: 32,
    height: 32,
    border: "3px solid #22243a",
    borderTopColor: "#4f6ef7",
    borderRadius: "50%",
    margin: "0 auto 16px",
    animation: "spin 0.8s linear infinite",
  },
};